/**
 * Canonical portal paths: client `/dashboard`, broker `/sales/dashboard`, admin `/admin/dashboard`, vendor `/peo/dashboard`.
 */

export const CLIENT_DASHBOARD_PATH = '/dashboard';

export const BROKER_DASHBOARD_BASE = '/sales/dashboard';

export const ADMIN_SHELL_BASE = '/admin/dashboard';

export const PEO_DASHBOARD_BASE = '/peo/dashboard';

/**
 * Login / post-auth landing helper: send the user to the Overview tab of a
 * dashboard shell. Leaves paths that already set `tab` unchanged (e.g. BDR CRM).
 */
export function withOverviewTab(path: string): string {
  const qIndex = path.indexOf('?');
  const search = qIndex >= 0 ? path.slice(qIndex + 1) : '';
  if (new URLSearchParams(search).has('tab')) return path;
  const base = qIndex >= 0 ? path.slice(0, qIndex) : path;
  return search ? `${base}?${search}&tab=overview` : `${base}?tab=overview`;
}

export type BrokerDashboardSegment = 'applications' | 'assignments' | 'quote-management' | 'clients';

/** Broker shell index or nested tool path (www / org subdomain). */
export function brokerDashboardPath(segment?: BrokerDashboardSegment): string {
  if (!segment) return BROKER_DASHBOARD_BASE;
  return `${BROKER_DASHBOARD_BASE}/${segment}`;
}

/** Super-admin org slug shell broker base. */
export function portalOrgBrokerDashboardBase(orgSlug: string): string {
  return `/portal/${orgSlug}/sales/dashboard`;
}

export function portalOrgBrokerDashboardPath(orgSlug: string, segment?: BrokerDashboardSegment): string {
  const base = portalOrgBrokerDashboardBase(orgSlug);
  if (!segment) return base;
  return `${base}/${segment}`;
}

/** True if pathname is broker shell on main host or under `/portal/:slug/sales/dashboard`. */
export function isBrokerDashboardPath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, '') || '/';
  if (isSubdomainBrokerDashboardPath(p)) return true;
  return /^\/portal\/[^/]+\/sales\/dashboard(\/.*)?$/.test(p);
}

/**
 * True only for the subdomain-based broker shell (`/sales/dashboard` and nested tool paths),
 * never the path-based super-admin route `/portal/:orgSlug/sales/dashboard`. Use this (not
 * `isBrokerDashboardPath`) when the caller needs to know whether the *hostname* is nested one
 * level under an org subdomain — the path-based `/portal/:orgSlug/...` route works on any host,
 * including a bare environment root with no subdomain nesting at all, so it must not be treated
 * as evidence of a subdomain preview.
 */
export function isSubdomainBrokerDashboardPath(pathname: string): boolean {
  const p = pathname.replace(/\/$/, '') || '/';
  return p === BROKER_DASHBOARD_BASE || p.startsWith(`${BROKER_DASHBOARD_BASE}/`);
}
